<script setup lang="ts">
import BusinessAnalyticsDrawer from '@/components/Drawers/Businesses/BusinessAnalyticsDrawer.vue'
import CalendarEventHandler from '@/components/Drawers/Calendar/CalendarEventHandler.vue'
import CustomerContactDataDrawer from '@/components/Drawers/Customers/CustomerContactDataDrawer.vue'
import ProductEventsDrawer from '@/components/Drawers/Events/ProductEventsDrawer.vue'
import ConfirmActionDialog from '@/components/Dialogs/ConfirmActionDialog.vue'
import HoardingPlanDialog from '@/components/Dialogs/HoardingPlanDialog.vue'
import Footer from '@/layouts/components/Footer.vue'
import MessagesComposerCoverageHarness from './components/MessagesComposerCoverageHarness.vue'
import InspectionHistoryCard from '@/views/apps/assets/inspections/preview/InspectionHistoryCard.vue'
import ServiceHistoryCard from '@/views/apps/assets/services/preview/ServiceHistoryCard.vue'
import LinkedCalendarEntriesCard from '@/components/Cards/Calendar/LinkedCalendarEntriesCard.vue'
import LinkedInvoicesCard from '@/components/Cards/Invoices/LinkedInvoicesCard.vue'
import LinkedOrdersCard from '@/components/Cards/Orders/LinkedOrdersCard.vue'
import LinkedQuotesCard from '@/components/Cards/Quotes/LinkedQuotesCard.vue'
import RecurringQuotesDialog from '@/components/Dialogs/Quotes/RecurringQuotesDialog.vue'
import FileManager from '@/views/Previews/Files/FileManager.vue'
import UserInvoiceTable from '@/views/apps/user/view/UserInvoiceTable.vue'
import ProjectList from '@/views/pages/user-profile/profile/ProjectList.vue'
import RecentDeviceLedger from '@/views/pages/account-settings/RecentDeviceLedger.vue'
import BillingHistoryTable from '@/views/pages/account-settings/BillingHistoryTable.vue'
import UserAccountProjectLedger from '@/views/apps/user/view/UserAccountProjectLedger.vue'
import UserSessionLedger from '@/views/apps/user/view/UserSessionLedger.vue'
import UserBillingAddressWorkspace from '@/views/apps/user/view/UserBillingAddressWorkspace.vue'
import type { UserBillingAddress } from '@/views/apps/user/view/userBillingAddressPresentation'
import UserNotificationPreferenceWorkspace from '@/components/NotificationPreferences/UserNotificationPreferenceWorkspace.vue'
import AccountNotificationPreferenceWorkspace from '@/components/NotificationPreferences/AccountNotificationPreferenceWorkspace.vue'
import ServiceTotalsForm from '@/components/Forms/ServiceTotals/ServiceTotalsForm.vue'
import type { ServiceTotalItem } from '@/components/Forms/ServiceTotals/ServiceTotalLineItem.vue'
import PaymentItemsSimple from '@/components/Forms/PaymentItemsSimple.vue'
import {
  copyNotificationPreferences,
  type NotificationChannel,
  type NotificationPreferenceRecord,
} from '@/components/NotificationPreferences/notificationPreferencePresentation'
import { Endpoint } from '@/@vxapp/@Enums/Endpoint'
import { useAuthStore } from '@/@vxapp/Auth/useAuthStore'
import { DrawingDirectionName } from '@/@vxapp/@Enums/HoardingEnums/DrawingDirection'
import { DrawingLine } from '@/@vxapp/@Enums/HoardingEnums/DrawingLine'
import { HoardingComponent } from '@/@vxapp/@Enums/HoardingEnums/HoardingEnums'
import { LabelPositionId } from '@/@vxapp/@Enums/HoardingEnums/LabelPosition'
import { useHoardingPlanStore } from '@/@vxapp/Hoardings/useHoardingPlanStore'
import navItems from '@/navigation/vertical'
import { MetaLayout } from '@/@vxapp/@Enums/MetaLayout'
import { Subject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { Action } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { QuoteField } from '@/@vxapp/@Enums/PHP/ServicesV2/Quotes/DataProviders/QuoteField'
import { createApplicationAbility } from '@/plugins/casl/abilityHelper'
import { SiteField } from '@/@vxapp/@Enums/PHP/ServicesV2/Sites/Enums/SiteField'
import { useCalendarStore } from '@/@vxapp/Calendar/useCalendarStore'
import { useCronStore } from '@/@vxapp/Cron/useCronStore'
import { useEventStore } from '@/@vxapp/Events/useEventStore'
import { useOrderStore } from '@/@vxapp/Orders/useOrderStore'
import { useProductStore } from '@/@vxapp/Products/useProductStore'
import { useQuoteStore } from '@/@vxapp/Quotes/useQuoteStore'
import { useRuleStore } from '@/@vxapp/Rule/useRuleStore'
import { useAssetInspectionStore } from '@/@vxapp/Assets/Inspections/useAssetInspectionStore'
import { useAssetServiceStore } from '@/@vxapp/Assets/Services/useAssetServiceStore'
import { useJobsStore } from '@/@vxapp/Jobs/useJobsStore'
import { useInvoiceStore } from '@/@vxapp/Invoices/useInvoiceStore'
import { filingCabinetStore } from '@/@vxapp/Files/filingCabinetStore'

definePage({
  meta: {
    subject: Subject.AUTH_LOGIN,
    action: 'read',
    layout: MetaLayout.Blank,
    notLoggedIn: true,
  },
})

interface DrawerVisibilityParams {
  visibility: boolean
}

interface CoverageContactData {
  serviceAttention?: string | null
  firstName?: string | null
  lastName?: string | null
  address?: string | null
  contactNumber?: string | null
  email?: string | null
  [key: string]: unknown
}

interface AnalyticsData {
  [SiteField.GOOGLE_ADS_ID]: string | null
  [SiteField.GOOGLE_TAG_MANAGER_ID]: string | null
  [SiteField.FACEBOOK_PIXEL_ID]: string | null
  [SiteField.TIKTOK_PIXEL_ID]: string | null
}

interface NavigationCoverageHeadingItem {
  id: string
  title: string
  kind: 'heading'
}

interface NavigationCoverageLinkItem {
  id: string
  title: string
  kind: 'link'
  to: string
}

interface CoverageSegmentOptions {
  ceilingHeightMm?: number
  elementId: string
  isLeftElementDoor?: boolean
  isRightElementDoor?: boolean
  lineSize: number
  lineType?: string
  orientation?: DrawingDirectionName
  flipInsideDirection?: boolean
  x1Mm: number
  x2Mm: number
  y1Mm?: number
  y2Mm?: number
}

type NavigationCoverageItem = NavigationCoverageHeadingItem | NavigationCoverageLinkItem

const isCoverageDialogVisible = ref(false)
const isCoverageDrawerOpen = ref(false)
const isCoverageCalendarDrawerOpen = ref(false)
const isCoverageCalendarRule = ref(false)
const isCoverageCustomerContactDrawerOpen = ref(false)
const isCoverageProductEventsDrawerOpen = ref(false)
const isHoardingPlannerVisible = ref(false)
const isRecurringQuotesDialogVisible = ref(false)
const dialogState = ref('closed')
const drawerState = ref('closed')
const calendarDrawerState = ref('closed')
const customerContactDrawerState = ref('closed')
const productEventsDrawerState = ref('closed')
const hoardingPlannerState = ref('closed')
const hoardingCoverageScenario = ref<'angled' | 'straight' | 'door'>('straight')
const projectCoverageScenario = ref<'empty' | 'error' | 'loading' | 'ready'>('ready')
const deviceCoverageScenario = ref<'empty' | 'error' | 'loading' | 'ready'>('ready')
const accountProjectCoverageScenario = ref<'empty' | 'error' | 'loading' | 'ready'>('ready')
const userSessionCoverageScenario = ref<'empty' | 'error' | 'loading' | 'ready'>('ready')
const billingAddressCoverageScenario = ref<'empty' | 'error' | 'loading' | 'ready'>('ready')
const billingAddressEditCount = ref(0)
const notificationCoverageScenario = ref<'empty' | 'error' | 'loading' | 'ready'>('ready')
const notificationSaveCount = ref(0)
const notificationTiming = ref("Only when I'm online")
const serviceTotalsCoverageScenario = ref<'empty' | 'error' | 'loading' | 'ready'>('ready')

const serviceTotalsCoverage = ref<Record<string, ServiceTotalItem[]>>({
  Labour: [
    {
      description: 'Site installation and commissioning',
      gross: '990.00',
      name: 'Installation',
      net: '900.00',
      qty: 1,
      sku: 'LAB-01',
      tax: '90.00',
      toolTip: '',
    },
  ],
  Materials: [
    {
      description: 'Fixings and consumables',
      gross: '165.00',
      name: 'Site materials',
      net: '150.00',
      qty: 1,
      sku: 'MAT-01',
      tax: '15.00',
      toolTip: '',
    },
  ],
})

const coverageCustomerContactData = ref<CoverageContactData>({
  serviceAttention: 'Coverage site',
  firstName: 'Jordan',
  lastName: 'Coverage',
  address: '10 Coverage Street',
  contactNumber: '0400 000 000',
  email: 'coverage@example.com',
})

const savedCustomerContactData = ref<CoverageContactData | null>(null)

const isPaymentCoverageEditing = ref(false)
const paymentCoverageDescription = ref('Annual platform service')
const paymentCoverageNet = ref<string | number>(1250)
const paymentCoverageTax = ref<string | number>(125)

const paymentCoverageServiceData = {
  booleanSettings: { includeGST: false },
}

const paymentCoverageMessages = {
  SERVICE_BILLING_GROSS: 1375,
  SERVICE_BILLING_NET: 1250,
  SERVICE_BILLING_TAX: 125,
}

const handlePaymentCoverageReadClick = () => {
  isPaymentCoverageEditing.value = false
}

const handlePaymentCoverageEditClick = () => {
  isPaymentCoverageEditing.value = true
}

const handlePaymentCoverageDescriptionUpdate = (description: string) => {
  paymentCoverageDescription.value = description
}

const visibleServiceTotalsCoverage = computed(() =>
  serviceTotalsCoverageScenario.value === 'empty' ? {} : serviceTotalsCoverage.value
)

const serviceTotalsCoverageError = computed(() =>
  serviceTotalsCoverageScenario.value === 'error' ? 'Invoice worksheet unavailable' : ''
)

const isServiceTotalsCoverageLoading = computed(
  () => serviceTotalsCoverageScenario.value === 'loading'
)

const handleServiceTotalsReadyClick = () => {
  serviceTotalsCoverageScenario.value = 'ready'
}

const handleServiceTotalsLoadingClick = () => {
  serviceTotalsCoverageScenario.value = 'loading'
}

const handleServiceTotalsEmptyClick = () => {
  serviceTotalsCoverageScenario.value = 'empty'
}

const handleServiceTotalsErrorClick = () => {
  serviceTotalsCoverageScenario.value = 'error'
}

const handleServiceTotalsUpdate = (serviceTotals: Record<string, ServiceTotalItem[]>) => {
  serviceTotalsCoverage.value = serviceTotals
}

const notificationCoverageSeed: NotificationPreferenceRecord[] = [
  { type: 'New for you', email: true, browser: false, app: false },
  { type: 'Account activity', email: false, browser: true, app: true },
  { type: 'A new browser used to sign in', email: true, browser: true, app: true },
  { type: 'A new device is linked', email: false, browser: true, app: false },
]

const notificationCoveragePreferences = ref(copyNotificationPreferences(notificationCoverageSeed))

const visibleNotificationCoveragePreferences = computed(() =>
  notificationCoverageScenario.value === 'empty' ? [] : notificationCoveragePreferences.value
)

const notificationCoverageError = computed(() =>
  notificationCoverageScenario.value === 'error' ? 'Notification settings unavailable' : ''
)

const isNotificationCoverageLoading = computed(
  () => notificationCoverageScenario.value === 'loading'
)

const handleNotificationCoverageReadyClick = () => {
  notificationCoverageScenario.value = 'ready'
}

const handleNotificationCoverageLoadingClick = () => {
  notificationCoverageScenario.value = 'loading'
}

const handleNotificationCoverageEmptyClick = () => {
  notificationCoverageScenario.value = 'empty'
}

const handleNotificationCoverageErrorClick = () => {
  notificationCoverageScenario.value = 'error'
}

const handleNotificationCoverageToggle = (
  type: string,
  channel: NotificationChannel,
  value: boolean
) => {
  const preference = notificationCoveragePreferences.value.find(item => item.type === type)

  if (preference) preference[channel] = value
}

const handleNotificationCoverageSave = () => {
  notificationSaveCount.value += 1
}

const handleNotificationCoverageReset = () => {
  notificationCoveragePreferences.value = copyNotificationPreferences(notificationCoverageSeed)
  notificationTiming.value = "Only when I'm online"
}

const handleNotificationTimingUpdate = (value: string) => {
  notificationTiming.value = value
}

const billingAddressCoverageRecord: UserBillingAddress = {
  address: '100 Water Plant Avenue, Building 1303 Wake Island',
  billingEmail: 'gertrude@gmail.com',
  companyName: 'Pixinvent',
  contact: '+1(609) 933-44-22',
  country: 'USA',
  state: 'Queensland',
  taxID: 'TAX-875623',
  vatNumber: 'SDF754K77',
  zipCode: '403114',
}

const billingAddressCoverageValue = computed(() =>
  billingAddressCoverageScenario.value === 'empty' ? undefined : billingAddressCoverageRecord
)

const billingAddressCoverageError = computed(() =>
  billingAddressCoverageScenario.value === 'error' ? 'Billing profile unavailable' : ''
)

const isBillingAddressCoverageLoading = computed(
  () => billingAddressCoverageScenario.value === 'loading'
)

const handleBillingAddressReadyClick = () => {
  billingAddressCoverageScenario.value = 'ready'
}

const handleBillingAddressLoadingClick = () => {
  billingAddressCoverageScenario.value = 'loading'
}

const handleBillingAddressEmptyClick = () => {
  billingAddressCoverageScenario.value = 'empty'
}

const handleBillingAddressErrorClick = () => {
  billingAddressCoverageScenario.value = 'error'
}

const handleBillingAddressEdit = () => {
  billingAddressEditCount.value += 1
}

const authStore = useAuthStore()
const calendarStore = useCalendarStore()
const cronStore = useCronStore()
const eventStore = useEventStore()
const hoardingPlanStore = useHoardingPlanStore()
const orderStore = useOrderStore()
const productStore = useProductStore()
const quoteStore = useQuoteStore()
const ruleStore = useRuleStore()
const inspectionStore = useAssetInspectionStore()
const assetServiceStore = useAssetServiceStore()
const jobStore = useJobsStore()
const invoiceStore = useInvoiceStore()
const fileManagerStore = filingCabinetStore()

const projectCoverageProjects = computed(() =>
  projectCoverageScenario.value === 'empty' ? [] : undefined
)

const projectCoverageError = computed(() =>
  projectCoverageScenario.value === 'error' ? 'Profile service unavailable' : ''
)

const isProjectCoverageLoading = computed(() => projectCoverageScenario.value === 'loading')

const handleProjectCoverageReadyClick = () => {
  projectCoverageScenario.value = 'ready'
}

const handleProjectCoverageLoadingClick = () => {
  projectCoverageScenario.value = 'loading'
}

const handleProjectCoverageEmptyClick = () => {
  projectCoverageScenario.value = 'empty'
}

const handleProjectCoverageErrorClick = () => {
  projectCoverageScenario.value = 'error'
}

const deviceCoverageDevices = computed(() =>
  deviceCoverageScenario.value === 'empty' ? [] : undefined
)

const deviceCoverageError = computed(() =>
  deviceCoverageScenario.value === 'error' ? 'Session service unavailable' : ''
)

const isDeviceCoverageLoading = computed(() => deviceCoverageScenario.value === 'loading')

const handleDeviceCoverageReadyClick = () => {
  deviceCoverageScenario.value = 'ready'
}

const handleDeviceCoverageLoadingClick = () => {
  deviceCoverageScenario.value = 'loading'
}

const handleDeviceCoverageEmptyClick = () => {
  deviceCoverageScenario.value = 'empty'
}

const handleDeviceCoverageErrorClick = () => {
  deviceCoverageScenario.value = 'error'
}

const accountProjectCoverageProjects = computed(() =>
  accountProjectCoverageScenario.value === 'empty' ? [] : undefined
)

const accountProjectCoverageError = computed(() =>
  accountProjectCoverageScenario.value === 'error' ? 'Project service unavailable' : ''
)

const isAccountProjectCoverageLoading = computed(
  () => accountProjectCoverageScenario.value === 'loading'
)

const handleAccountProjectCoverageReadyClick = () => {
  accountProjectCoverageScenario.value = 'ready'
}

const handleAccountProjectCoverageLoadingClick = () => {
  accountProjectCoverageScenario.value = 'loading'
}

const handleAccountProjectCoverageEmptyClick = () => {
  accountProjectCoverageScenario.value = 'empty'
}

const handleAccountProjectCoverageErrorClick = () => {
  accountProjectCoverageScenario.value = 'error'
}

const userSessionCoverageDevices = computed(() =>
  userSessionCoverageScenario.value === 'empty' ? [] : undefined
)

const userSessionCoverageError = computed(() =>
  userSessionCoverageScenario.value === 'error' ? 'Session service unavailable' : ''
)

const isUserSessionCoverageLoading = computed(() => userSessionCoverageScenario.value === 'loading')

const handleUserSessionCoverageReadyClick = () => {
  userSessionCoverageScenario.value = 'ready'
}

const handleUserSessionCoverageLoadingClick = () => {
  userSessionCoverageScenario.value = 'loading'
}

const handleUserSessionCoverageEmptyClick = () => {
  userSessionCoverageScenario.value = 'empty'
}

const handleUserSessionCoverageErrorClick = () => {
  userSessionCoverageScenario.value = 'error'
}

const coverageAuthStore = authStore as {
  userData: null | {
    userGUID: string
  }
}

const coverageCalendarStore = calendarStore as any
const coverageCronStore = cronStore as any
const coverageEventStore = eventStore as any
const coverageOrderStore = orderStore as any
const coverageProductStore = productStore as any
const coverageQuoteStore = quoteStore as any
const coverageRuleStore = ruleStore as any
const coverageInspectionStore = inspectionStore as any
const coverageAssetServiceStore = assetServiceStore as any
const coverageJobStore = jobStore as any
const coverageInvoiceStore = invoiceStore as any
const coverageFileManagerStore = fileManagerStore as any

const fileManagerRequestCount = ref(0)
const userInvoiceRequestCount = ref(0)
const billingHistoryRequestCount = ref(0)
const isBillingHistoryMounted = ref(false)
const billingHistoryCoverageKey = ref(0)
const billingHistoryScenario = ref<'empty' | 'error' | 'loading' | 'ready'>('ready')

const mountBillingHistoryScenario = (scenario: 'empty' | 'error' | 'loading' | 'ready') => {
  billingHistoryScenario.value = scenario
  isBillingHistoryMounted.value = true
  billingHistoryCoverageKey.value += 1
}

const handleBillingHistoryReadyClick = () => {
  mountBillingHistoryScenario('ready')
}

const handleBillingHistoryLoadingClick = () => {
  mountBillingHistoryScenario('loading')
}

const handleBillingHistoryEmptyClick = () => {
  mountBillingHistoryScenario('empty')
}

const handleBillingHistoryErrorClick = () => {
  mountBillingHistoryScenario('error')
}

const coverageFileManagerRootItems = [
  {
    id: 'contracts',
    type: 'contracts',
    fileType: 'contracts',
    displayName: 'Contracts',
    description: 'Signed agreements and project paperwork',
    isDirectoryItem: true,
    itemIcon: 'tabler-folder',
    actions: {
      uploadFile: {
        color: 'primary',
        icon: 'tabler-cloud-upload',
        toolTip: 'Upload into Contracts',
        onClick: 'uploadFile',
        data: {
          directory: {
            type: 'contracts',
            displayName: 'Contracts',
            description: 'Signed agreements and project paperwork',
          },
          fileUploadEndpoint: '/coverage/files/contracts',
        },
      },
    },
  },
  {
    id: 'site-plan',
    fileGUID: 'coverage-site-plan-guid',
    fileType: 'PDF',
    displayName: 'Approved site plan',
    name: 'approved-site-plan.pdf',
    uploaded_on: '2026-07-21 14:35:00',
    size: 2.4,
    itemIcon: 'tabler-file-type-pdf',
    isFileItem: true,
    actions: {},
  },
  {
    id: 'photos',
    type: 'photos',
    fileType: 'photos',
    displayName: 'Site photos',
    description: 'Installation and completion photos',
    isDirectoryItem: true,
    itemIcon: 'tabler-folder',
    actions: {},
  },
]

const coverageContractItems = [
  {
    id: 'back',
    displayName: 'All files',
    name: 'Back',
    itemIcon: 'tabler-arrow-left',
    isBackButton: true,
    actions: {},
  },
  {
    id: 'signed-contract',
    fileGUID: 'coverage-contract-guid',
    fileType: 'PDF',
    displayName: 'Signed customer contract',
    name: 'signed-customer-contract.pdf',
    uploaded_on: '2026-07-22 10:15:00',
    size: 1.8,
    itemIcon: 'tabler-file-type-pdf',
    isFileItem: true,
    actions: {},
  },
]

coverageFileManagerStore.useFileManager = async (_entryGUID: string, directoryFileType = '') => {
  fileManagerRequestCount.value += 1

  const isContractsDirectory = directoryFileType === 'contracts'
  const items = isContractsDirectory ? coverageContractItems : coverageFileManagerRootItems

  coverageFileManagerStore.fmTitle = isContractsDirectory
    ? 'Contract library'
    : 'Project file library'
  coverageFileManagerStore.fmSubtitle = isContractsDirectory
    ? 'Review the current signed agreements.'
    : 'Browse project documents, folders, and site media.'
  coverageFileManagerStore.items = items
  coverageFileManagerStore.totalItems = items.length

  return {
    responseCode: 200,
    items,
    totalItems: items.length,
  }
}

coverageAuthStore.userData = coverageAuthStore.userData ?? {
  userGUID: 'playwright-coverage-user',
}

const analyticsData = ref<AnalyticsData>({
  [SiteField.GOOGLE_ADS_ID]: 'AW-123456789',
  [SiteField.GOOGLE_TAG_MANAGER_ID]: 'GTM-ABC1234',
  [SiteField.FACEBOOK_PIXEL_ID]: '123456789012345',
  [SiteField.TIKTOK_PIXEL_ID]: '987654321098765432',
})

const coverageCalendarEvent = ref({
  calendarGUID: 'coverage-calendar-guid',
  calendarTitle: 'Coverage Install Visit',
  calendarDescription: 'Deterministic calendar drawer coverage event',
  calendarInstallDate: '2026-06-28 09:00:00',
  calendarRemovalDate: '2026-06-28 12:00:00',
  booleanSettings: {
    isAllDay: false,
  },
  jobData: {},
  ruleGUIDs: [],
  productGUID: null,
  jobTypeID: 1,
})

const coverageOrderData = {
  orderGUID: 'coverage-order-guid',
}

const coverageAssetData = {
  assetGUID: 'coverage-asset-guid',
  name: 'Coverage Forklift',
  type: 'Plant',
  serviceSettings: { serviceFrequencyMetric: 'kms' },
}

const coverageCalendarJobData = ref({
  jobGUID: 'coverage-job-guid',
  linkedCalendarEntryGUIDs: ['coverage-calendar-linked'],
  linkedInvoiceGUIDs: ['coverage-invoice-linked'],
  linkedOrderGUIDs: ['coverage-order-linked'],
  linkedQuoteGUIDs: ['coverage-quote-linked'],
})

const coverageCalendarEntries = [
  {
    id: 11,
    calendarGUID: 'coverage-calendar-linked',
    calendarTitle: 'Site measure',
    calendarDescription: 'Confirm access and final dimensions.',
    calendarInstallDate: '2026-07-24 09:00:00',
    calendarRemovalDate: '2026-07-24 10:30:00',
    status: 'Confirmed',
    jobTypeID: 1,
    attendees: [{ id: 'coverage-attendee' }],
  },
  {
    id: 12,
    calendarGUID: 'coverage-calendar-candidate',
    calendarTitle: 'Installation window',
    calendarDescription: 'Reserved installation window.',
    calendarInstallDate: '2026-07-28 13:00:00',
    calendarRemovalDate: '2026-07-28 16:00:00',
    status: 'Tentative',
    jobTypeID: 1,
  },
]

const coverageInvoices = [
  {
    id: 201,
    invoiceGUID: 'coverage-invoice-linked',
    invoiceStatus: 'unpaid',
    invoiceDescription: 'Site measure progress claim',
    invoiceDate: '2026-07-20',
    invoiceDueDate: '2026-08-03',
    contactData: { name: 'Northwind Fitouts', serviceAttention: 'Harbour Site' },
    SERVICE_BILLING_GROSS: 1870,
    SERVICE_BILLING_NET: 1700,
    SERVICE_BILLING_TAX: 170,
  },
  {
    id: 202,
    invoiceGUID: 'coverage-invoice-candidate',
    invoiceStatus: 'pendingPayment',
    invoiceDescription: 'Installation milestone',
    invoiceDate: '2026-07-22',
    invoiceDueDate: '2026-08-05',
    contactData: { name: 'Contoso Projects', serviceAttention: 'Museum Works' },
    SERVICE_BILLING_GROSS: 3520,
    SERVICE_BILLING_NET: 3200,
    SERVICE_BILLING_TAX: 320,
  },
]

const coverageLinkedOrders = [
  {
    id: 301,
    orderGUID: 'coverage-order-linked',
    status: 'dispatch',
    productGUID: 'coverage-product-linked',
    contactData: {
      name: 'Northwind Fitouts',
      serviceAttention: 'Harbour Site',
      address: '100 Harbour Street',
    },
    SERVICE_DATE: '2026-07-25',
    SERVICE_BILLING_GROSS: 4400,
    SERVICE_BILLING_NET: 4000,
    SERVICE_BILLING_TAX: 400,
  },
  {
    id: 302,
    orderGUID: 'coverage-order-candidate',
    status: 'scheduled',
    productGUID: 'coverage-product-candidate',
    contactData: {
      name: 'Contoso Projects',
      serviceAttention: 'Museum Works',
      address: '20 Gallery Lane',
    },
    SERVICE_DATE: '2026-07-29',
    SERVICE_BILLING_GROSS: 2860,
    SERVICE_BILLING_NET: 2600,
    SERVICE_BILLING_TAX: 260,
  },
]

const coverageLinkedQuotes = [
  {
    id: 401,
    quoteGUID: 'coverage-quote-linked',
    quoteStatus: 'active',
    quoteDate: '2026-07-24',
    contactData: { name: 'Northwind Fitouts', serviceAttention: 'Harbour Site' },
    jobData: {
      name: 'Hoarding installation proposal',
      installDate: '2026-08-01',
      number: 'JOB-401',
    },
    SERVICE_BILLING_GROSS: 4950,
    SERVICE_BILLING_NET: 4500,
    SERVICE_BILLING_TAX: 450,
  },
  {
    id: 402,
    quoteGUID: 'coverage-quote-candidate',
    quoteStatus: 'pendingConfirmation',
    quoteDate: '2026-07-25',
    contactData: { name: 'Contoso Projects', serviceAttention: 'Museum Works' },
    jobData: {
      name: 'Museum access proposal',
      installDate: '2026-08-05',
      number: 'JOB-402',
    },
    SERVICE_BILLING_GROSS: 3190,
    SERVICE_BILLING_NET: 2900,
    SERVICE_BILLING_TAX: 290,
  },
]

const coverageRecurringQuotes = [
  {
    id: 501,
    quoteGUID: 'coverage-recurring-quote',
    status: 'active',
    dailyRate: 175,
    startDate: '2026-07-01',
    endDate: '2026-12-31',
    lastBillingDate: '2026-07-23',
    contactData: { name: 'Northwind Fitouts', serviceAttention: 'Harbour Site' },
    jobData: { name: 'Daily access hire' },
  },
]

const seedCalendarLinkingCoverageStores = () => {
  coverageJobStore.job = coverageCalendarJobData.value
  coverageJobStore.linkedCalendarEntries = [coverageCalendarEntries[0]]
  coverageJobStore.calendarPaginationMeta = {
    entriesFound: 1,
    currentPage: 1,
    totalPages: 1,
  }
  coverageJobStore.calendarQueryParams = { page: 1, limit: 10 }
  coverageJobStore.fetchLinkedCalendarEntries = async () => {
    const linkedGuids = coverageJobStore.job?.linkedCalendarEntryGUIDs ?? []

    coverageJobStore.linkedCalendarEntries = coverageCalendarEntries.filter(entry =>
      linkedGuids.includes(entry.calendarGUID)
    )
    coverageJobStore.calendarPaginationMeta = {
      entriesFound: coverageJobStore.linkedCalendarEntries.length,
      currentPage: 1,
      totalPages: 1,
    }

    return { responseCode: 200 }
  }
  coverageJobStore.update = async (jobData: Record<string, any>) => {
    coverageJobStore.job = JSON.parse(JSON.stringify(jobData))
    coverageCalendarJobData.value = JSON.parse(JSON.stringify(jobData))

    return { job: coverageJobStore.job, responseCode: 200 }
  }
  coverageCalendarStore.fetchAll = async (queryParams: Record<string, any>) => ({
    entries: coverageCalendarEntries,
    paginationMeta: { entriesFound: 2, currentPage: 1, totalPages: 1 },
    queryParams,
    responseCode: 200,
  })
  coverageCalendarStore.fetchAutoCompletes = async () => {
    coverageCalendarStore.autoCompletes = {}
  }
  coverageCalendarStore.fetchCreationSettings = async () => {
    coverageCalendarStore.creationSettings = {}
  }
}

const seedInvoiceLinkingCoverageStores = () => {
  coverageJobStore.linkedInvoices = [coverageInvoices[0]]
  coverageJobStore.invoicesPaginationMeta = {
    entriesFound: 1,
    currentPage: 1,
    totalPages: 1,
  }
  coverageJobStore.invoicesQueryParams = { page: 1, limit: 10 }
  coverageJobStore.fetchLinkedInvoices = async () => {
    const linkedGuids = coverageJobStore.job?.linkedInvoiceGUIDs ?? []

    coverageJobStore.linkedInvoices = coverageInvoices.filter(invoice =>
      linkedGuids.includes(invoice.invoiceGUID)
    )
    coverageJobStore.invoicesPaginationMeta = {
      entriesFound: coverageJobStore.linkedInvoices.length,
      currentPage: 1,
      totalPages: 1,
    }

    return { responseCode: 200 }
  }
  coverageInvoiceStore.serviceStyles = {}
  coverageInvoiceStore.fetchAll = async (queryParams: Record<string, any>) => {
    if (isBillingHistoryMounted.value) {
      billingHistoryRequestCount.value += 1
    } else {
      userInvoiceRequestCount.value += 1
    }

    if (isBillingHistoryMounted.value && billingHistoryScenario.value === 'loading') {
      return new Promise(() => {})
    }

    if (isBillingHistoryMounted.value && billingHistoryScenario.value === 'error') {
      return {
        message: 'Billing service unavailable',
        responseCode: 500,
      }
    }

    const visibleInvoices =
      isBillingHistoryMounted.value && billingHistoryScenario.value === 'empty'
        ? []
        : coverageInvoices

    coverageInvoiceStore.paginationMeta = {
      entriesFound: visibleInvoices.length ? 12 : 0,
      currentPage: 1,
      totalPages: visibleInvoices.length ? 2 : 0,
    }
    coverageInvoiceStore.queryParams = queryParams

    return {
      invoices: visibleInvoices,
      responseCode: visibleInvoices.length ? 200 : 404,
    }
  }
  coverageInvoiceStore.deleteInvoice = async (invoiceGUID: string) => {
    const invoiceIndex = coverageInvoices.findIndex(invoice => invoice.invoiceGUID === invoiceGUID)

    if (invoiceIndex >= 0) {
      coverageInvoices.splice(invoiceIndex, 1)

      return { responseCode: 204 }
    }

    return { error: 'Invoice not found', responseCode: 404 }
  }
}

const seedOrderLinkingCoverageStores = () => {
  coverageJobStore.linkedOrders = [coverageLinkedOrders[0]]
  coverageJobStore.ordersPaginationMeta = {
    entriesFound: 1,
    currentPage: 1,
    totalPages: 1,
  }
  coverageJobStore.ordersQueryParams = { page: 1, limit: 10 }
  coverageJobStore.fetchLinkedOrders = async () => {
    const linkedGuids = coverageJobStore.job?.linkedOrderGUIDs ?? []

    coverageJobStore.linkedOrders = coverageLinkedOrders.filter(order =>
      linkedGuids.includes(order.orderGUID)
    )
    coverageJobStore.ordersPaginationMeta = {
      entriesFound: coverageJobStore.linkedOrders.length,
      currentPage: 1,
      totalPages: 1,
    }

    return { responseCode: 200 }
  }
  coverageOrderStore.serviceStyles = {}
  coverageOrderStore.fetchAll = async (queryParams: Record<string, any>) => {
    coverageOrderStore.paginationMeta = {
      entriesFound: coverageLinkedOrders.length,
      currentPage: 1,
      totalPages: 1,
    }
    coverageOrderStore.queryParams = queryParams

    return {
      orders: coverageLinkedOrders,
      responseCode: 200,
    }
  }
  coverageProductStore.productsCategorizedByGUID = {
    'coverage-product-linked': {
      PRODUCT_DISPLAY_NAME: 'Hoarding installation',
      PRODUCT_MEDIA_THUMBNAIL_URL: null,
    },
    'coverage-product-candidate': {
      PRODUCT_DISPLAY_NAME: 'Overnight delivery',
      PRODUCT_MEDIA_THUMBNAIL_URL: null,
    },
  }
  coverageProductStore.fetchAllCategorizedByGUID = async () =>
    coverageProductStore.productsCategorizedByGUID
}

const seedQuoteLinkingCoverageStores = () => {
  coverageJobStore.linkedQuotes = [coverageLinkedQuotes[0]]
  coverageJobStore.quotesPaginationMeta = {
    entriesFound: 1,
    currentPage: 1,
    totalPages: 1,
  }
  coverageJobStore.quotesQueryParams = { page: 1, limit: 10 }
  coverageJobStore.fetchLinkedQuotes = async () => {
    const linkedGuids = coverageJobStore.job?.linkedQuoteGUIDs ?? []

    coverageJobStore.linkedQuotes = coverageLinkedQuotes.filter(quote =>
      linkedGuids.includes(quote.quoteGUID)
    )
    coverageJobStore.quotesPaginationMeta = {
      entriesFound: coverageJobStore.linkedQuotes.length,
      currentPage: 1,
      totalPages: 1,
    }

    return { responseCode: 200 }
  }
  coverageQuoteStore.serviceStyles = {}
  coverageQuoteStore.fetchAll = async (queryParams: Record<string, any>) => {
    coverageQuoteStore.paginationMeta = {
      entriesFound: coverageLinkedQuotes.length,
      currentPage: 1,
      totalPages: 1,
    }
    coverageQuoteStore.queryParams = queryParams

    return {
      quotes: coverageLinkedQuotes,
      responseCode: 200,
    }
  }
}

const seedRecurringQuoteCoverageStores = () => {
  coverageQuoteStore.fetchRecurringQuotes = async (queryParams: Record<string, any>) => {
    coverageQuoteStore.paginationMeta = {
      entriesFound: coverageRecurringQuotes.length,
      currentPage: 1,
      totalPages: 1,
    }
    coverageQuoteStore.queryParams = queryParams

    return {
      recurringQuotes: coverageRecurringQuotes,
      responseCode: 200,
    }
  }
  coverageQuoteStore.setRecurringQuoteStatus = async (recurringQuote: Record<string, any>) => {
    const index = coverageRecurringQuotes.findIndex(entry => entry.id === recurringQuote.id)

    if (index >= 0) coverageRecurringQuotes[index] = { ...recurringQuote } as any

    return { responseCode: 200 }
  }
  coverageCronStore.generateRecurringQuotes = async () => ({
    data: { count: 1 },
    responseCode: 201,
  })
}

const seedAssetHistoryCoverageStores = () => {
  coverageInspectionStore.inspections = [
    {
      id: 1,
      inspectionGUID: 'coverage-inspection-1',
      inspectionName: 'Annual safety inspection',
      inspectionDate: '2026-07-01',
      inspectionNotes: '<p>Hydraulics and emergency stop verified.</p>',
    },
  ]
  coverageInspectionStore.paginationMeta = { entriesFound: 1, currentPage: 1, totalPages: 1 }
  coverageInspectionStore.fetchAll = async (queryParams: Record<string, unknown>) => ({
    inspections: coverageInspectionStore.inspections,
    paginationMeta: coverageInspectionStore.paginationMeta,
    queryParams,
    responseCode: 200,
  })
  coverageAssetServiceStore.services = [
    {
      id: 1,
      serviceGUID: 'coverage-service-1',
      serviceName: 'Hydraulic service',
      serviceDate: '2026-07-02',
      serviceMetric: 'kms',
      serviceValue: 124000,
      serviceNotes: 'Fluid and seals replaced.',
    },
  ]
  coverageAssetServiceStore.paginationMeta = { entriesFound: 1, currentPage: 1, totalPages: 1 }
  coverageAssetServiceStore.fetchAll = async (queryParams: Record<string, unknown>) => ({
    services: coverageAssetServiceStore.services,
    paginationMeta: coverageAssetServiceStore.paginationMeta,
    queryParams,
    responseCode: 200,
  })
}

seedCalendarLinkingCoverageStores()
seedInvoiceLinkingCoverageStores()
seedOrderLinkingCoverageStores()
seedQuoteLinkingCoverageStores()
seedRecurringQuoteCoverageStores()
seedAssetHistoryCoverageStores()

const closeCoverageDrawers = () => {
  isCoverageDrawerOpen.value = false
  isCoverageCalendarDrawerOpen.value = false
  isCoverageCustomerContactDrawerOpen.value = false
  isCoverageProductEventsDrawerOpen.value = false
  drawerState.value = 'closed'
  calendarDrawerState.value = 'closed'
  customerContactDrawerState.value = 'closed'
  productEventsDrawerState.value = 'closed'
}

const handleRecurringQuotesDialogVisibilityUpdate = (value: boolean) => {
  isRecurringQuotesDialogVisible.value = value
}

const handleRecurringQuotesDialogOpenButtonClick = () => {
  isRecurringQuotesDialogVisible.value = true
}

const seedProductEventsCoverageStores = () => {
  coverageProductStore.unformattedEventList = [{ eventGUID: 'coverage-event-1' }]
  coverageProductStore.formattedEventListByProductCategory = {
    install: {
      productEventCategoryName: 'Install',
      configuredEvents: [
        {
          eventGUID: 'coverage-event-1',
          requiredParams: [],
          name: 'Coverage Product Event',
          description: 'Product drawer coverage event',
          isEnabled: true,
        },
      ],
    },
  }
  coverageProductStore.product = {
    displayName: 'Coverage Product',
    productName: 'Coverage Product',
  }
  coverageProductStore.fetchAvailableProductEvents = async () => ({
    unformattedEventList: coverageProductStore.unformattedEventList,
    formattedEventListByProductCategory: coverageProductStore.formattedEventListByProductCategory,
  })
  coverageProductStore.fetch = async () => ({
    product: coverageProductStore.product,
    messagesVariables: {},
    responseCode: 200,
  })
  coverageProductStore.update = async () => ({
    product: coverageProductStore.product,
    vue: {},
    responseCode: 200,
  })
  coverageEventStore.processEvent = async () => ({
    data: {
      status: 'success',
      message: 'Processed',
    },
    responseCode: 200,
  })
}

const seedCalendarCoverageStores = () => {
  coverageCalendarStore.availableJobTypes = [
    {
      id: 1,
      title: 'Install',
      color: '16,185,129',
    },
  ]
  coverageCalendarStore.create = async () => ({
    entry: null,
    serviceStyles: null,
    vue: {},
    responseCode: 201,
    message: 'Created',
  })
  coverageCalendarStore.update = async () => ({
    entry: null,
    serviceStyles: null,
    vue: {},
    responseCode: 200,
    message: 'Updated',
  })
  coverageCalendarStore.fetchJobTypeSelections = async () => ({
    availableJobTypes: coverageCalendarStore.availableJobTypes,
    selectedJobTypes: [],
  })
  coverageRuleStore.rules = [
    {
      id: 1,
      ruleGUID: 'coverage-rule-1',
      ruleName: 'Coverage schedule guard',
      trigger: 'calendar.created',
      description: 'Keeps the coverage calendar pipeline inspectable.',
      conditions: { coverage: true },
      ruleActions: ['validate-window'],
      isActive: true,
    },
  ]
  coverageRuleStore.paginationMeta = { entriesFound: 1, currentPage: 1, totalPages: 1 }
  coverageRuleStore.fetchAll = async () => ({
    rules: coverageRuleStore.rules,
    serviceStyles: null,
    paginationMeta: {},
    queryParams: {},
    responseCode: 200,
  })
  coverageProductStore.products = []
  coverageProductStore.fetchAll = async () => ({
    products: coverageProductStore.products,
    serviceStyles: null,
    paginationMeta: {},
    queryParams: {},
    responseCode: 200,
  })
  coverageProductStore.fetchVisibleCategorizedByGUID = async () => null
  coverageOrderStore.orders = []
  coverageOrderStore.fetchAll = async () => ({
    message: 'Loaded',
    orders: coverageOrderStore.orders,
    responseCode: 200,
  })
  coverageQuoteStore.quotes = []
  coverageQuoteStore.fetchAll = async () => ({
    message: 'Loaded',
    quotes: coverageQuoteStore.quotes,
    responseCode: 200,
  })
}

function toNavigationCoverageId(value: string): string {
  return value
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

const navigationCoverageItems = computed<NavigationCoverageItem[]>(() => {
  const navigationItems: NavigationCoverageItem[] = []

  for (const item of navItems) {
    if ('heading' in item && typeof item.heading === 'string') {
      navigationItems.push({
        id: toNavigationCoverageId(item.heading),
        title: item.heading,
        kind: 'heading',
      } satisfies NavigationCoverageHeadingItem)

      continue
    }

    if ('children' in item && Array.isArray(item.children)) {
      for (const child of item.children) {
        if (!('title' in child && 'to' in child && typeof child.to === 'string')) {
          continue
        }

        navigationItems.push({
          id: toNavigationCoverageId(String(child.title)),
          title: String(child.title),
          kind: 'link',
          to: child.to,
        } satisfies NavigationCoverageLinkItem)
      }

      continue
    }

    if ('title' in item && 'to' in item && typeof item.to === 'string') {
      navigationItems.push({
        id: toNavigationCoverageId(String(item.title)),
        title: String(item.title),
        kind: 'link',
        to: item.to,
      } satisfies NavigationCoverageLinkItem)
    }
  }

  return navigationItems
})

const handleDrawerVisibilityUpdate = ({ visibility }: DrawerVisibilityParams) => {
  isCoverageDrawerOpen.value = visibility
  drawerState.value = visibility ? 'open' : 'closed'
}

const handleCalendarDrawerVisibilityUpdate = (visibility: boolean) => {
  isCoverageCalendarDrawerOpen.value = visibility
  calendarDrawerState.value = visibility ? 'open' : 'closed'
}

const handleCustomerContactDrawerVisibilityUpdate = ({ visibility }: DrawerVisibilityParams) => {
  isCoverageCustomerContactDrawerOpen.value = visibility
  customerContactDrawerState.value = visibility ? 'open' : 'closed'
}

const handleCustomerContactDataUpdate = (contactData: CoverageContactData) => {
  coverageCustomerContactData.value = contactData
  savedCustomerContactData.value = contactData
}

const handleProductEventsDrawerVisibilityUpdate = (visibility: boolean) => {
  isCoverageProductEventsDrawerOpen.value = visibility
  productEventsDrawerState.value = visibility ? 'open' : 'closed'
}

const closeCoverageCalendarDrawer = () => {
  handleCalendarDrawerVisibilityUpdate(false)
}

const closeCoverageProductEventsDrawer = () => {
  handleProductEventsDrawerVisibilityUpdate(false)
}

const handleDialogVisibilityUpdate = (hasAgreed: boolean) => {
  dialogState.value = hasAgreed ? 'confirmed' : 'closed'
  isCoverageDialogVisible.value = false
}

const handleDrawerOpenButtonClick = () => {
  closeCoverageDrawers()
  isCoverageDrawerOpen.value = true
  drawerState.value = 'open'
}

const handleCalendarDrawerOpenButtonClick = () => {
  seedCalendarCoverageStores()
  closeCoverageDrawers()
  isCoverageCalendarRule.value = false
  isCoverageCalendarDrawerOpen.value = true
  calendarDrawerState.value = 'open'
}

const handleCustomerContactDrawerOpenButtonClick = () => {
  closeCoverageDrawers()
  isCoverageCustomerContactDrawerOpen.value = true
  customerContactDrawerState.value = 'open'
}

const handleRuleDrawerOpenButtonClick = () => {
  seedCalendarCoverageStores()
  closeCoverageDrawers()
  isCoverageCalendarRule.value = true
  isCoverageCalendarDrawerOpen.value = true
  calendarDrawerState.value = 'open'
}

const handleProductEventsDrawerOpenButtonClick = () => {
  seedProductEventsCoverageStores()
  closeCoverageDrawers()
  isCoverageProductEventsDrawerOpen.value = true
  productEventsDrawerState.value = 'open'
}

const handleDialogOpenButtonClick = () => {
  isCoverageDialogVisible.value = true
  dialogState.value = 'open'
}

function createCoverageSegment({
  ceilingHeightMm = 2950,
  elementId,
  isLeftElementDoor = false,
  isRightElementDoor = false,
  lineSize,
  lineType = DrawingLine.panels.name,
  orientation = DrawingDirectionName.HORIZONTAL,
  flipInsideDirection = false,
  x1Mm,
  x2Mm,
  y1Mm = 0,
  y2Mm = 0,
}: CoverageSegmentOptions) {
  return {
    elementId,
    lineType,
    orientation,
    direction: orientation,
    lineSize,
    runLengthMm: lineSize,
    panelHeightMm: ceilingHeightMm,
    topSkirtingHeightMm: 130,
    bottomSkirtingHeightMm: 130,
    ceilingHeightMm,
    panelThicknessMm: 120,
    x1Mm,
    y1Mm,
    x2Mm,
    y2Mm,
    flipInsideDirection,
    isLeftElementDoor,
    isRightElementDoor,
    labelSettings: {
      position: LabelPositionId.CENTER,
      isVisible: {
        length: true,
        height: true,
        width: true,
      },
    },
    componentsRequired: {
      panelJoinUpright: true,
      offsetUprightLeft: true,
      offsetUprightRight: true,
    },
    additionalWeightKg: 0,
  }
}

const seedHoardingCoveragePlan = () => {
  const planConfig = hoardingPlanStore.getPlanConfigTemplate()
  const skirtingConfig = hoardingPlanStore.getSkirtingConfigTemplate()
  const checklistData = hoardingPlanStore.getChecklistDataTemplate()

  let pathSegments =
    hoardingCoverageScenario.value === 'angled'
      ? [
          createCoverageSegment({
            elementId: 'coverage-panel-angled-index-zero',
            lineSize: 6000,
            orientation: DrawingDirectionName.ANGLE,
            x1Mm: 0,
            y1Mm: 0,
            x2Mm: 4800,
            y2Mm: -3600,
          }),
        ]
      : [
          createCoverageSegment({
            elementId: 'coverage-panel-13800',
            lineSize: 13800,
            x1Mm: 0,
            x2Mm: 13800,
          }),
        ]

  if (hoardingCoverageScenario.value === 'door') {
    pathSegments = [
      createCoverageSegment({
        elementId: 'coverage-panel-left',
        lineSize: 2400,
        x1Mm: 0,
        x2Mm: 2400,
        isRightElementDoor: true,
      }),
      createCoverageSegment({
        elementId: 'coverage-door',
        lineType: DrawingLine.doors.name,
        lineSize: 1700,
        x1Mm: 2400,
        x2Mm: 4100,
      }),
      createCoverageSegment({
        elementId: 'coverage-panel-right',
        lineSize: 5400,
        x1Mm: 4100,
        x2Mm: 9500,
        isLeftElementDoor: true,
      }),
    ]
  }

  hoardingPlanStore.plan = {
    serviceType: Endpoint.Quotes.Name,
    serviceGUID: 'coverage-quote-guid',
    planGUID: 'coverage-plan-guid',
    title: 'Coverage Hoarding Plan',
    description: 'Deterministic Playwright hoarding planner harness with lintel and jamb coverage',
    ts: Date.now(),
    owner: 'playwright-coverage-user',
    pathSegments,
    cameraSnapshots: [],
    skirtingConfig,
    planConfig,
    checklistData,
    created_at: '2026-06-22 00:00:00',
    updated_at: null,
  }
  hoardingPlanStore.planGUID = 'coverage-plan-guid'
  hoardingPlanStore.pathSegments = pathSegments
  hoardingPlanStore.validatedSegments = pathSegments
  hoardingPlanStore.drawingConfig.dragToDraw = false
  hoardingPlanStore.isLoading = false
  hoardingPlanStore.setTheme('dark')
  hoardingPlanStore.setCurrentView(0)
  hoardingPlanStore.setLastLoaded('coverage-plan-guid')
  hoardingPlanStore.setLastSaved('coverage-plan-guid', Date.now())
  coverageQuoteStore.quote = {
    quoteGUID: hoardingPlanStore.plan.serviceGUID,
    jobData: { hoardingPlans: [hoardingPlanStore.plan] },
  }

  const coverageAbility = createApplicationAbility([
    ...(authStore.permissions ?? []),
    {
      action: Action.READ,
      subject: Subject.QUOTES_PREVIEW_GUID,
      fields: [QuoteField.JOB_DATA],
      enabled: true,
    },
  ])

  if (!authStore.ability) {
    authStore.ability = coverageAbility

    return
  }
  authStore.ability.update(coverageAbility.rules)
}

const handleHoardingPlannerOpenButtonClick = () => {
  hoardingCoverageScenario.value = 'straight'
  seedHoardingCoveragePlan()
  isHoardingPlannerVisible.value = true
  hoardingPlannerState.value = 'open'
}

const handleAngledHoardingPlannerOpenButtonClick = () => {
  hoardingCoverageScenario.value = 'angled'
  seedHoardingCoveragePlan()
  isHoardingPlannerVisible.value = true
  hoardingPlannerState.value = 'open'
}

const handleDoorHoardingPlannerOpenButtonClick = () => {
  hoardingCoverageScenario.value = 'door'
  seedHoardingCoveragePlan()
  isHoardingPlannerVisible.value = true
  hoardingPlannerState.value = 'open'
}

const handlePrintChecklistHoardingPlannerOpenButtonClick = () => {
  handleDoorHoardingPlannerOpenButtonClick()
  hoardingPlanStore.setChecklistLoadingMode(true, {
    [HoardingComponent.PANELS]: [
      {
        title: 'Frozen delivery',
        total: 150,
        items: Array.from({ length: 150 }, (_, index) => ({
          title: `Frozen panel line ${index + 1}`,
          total: index + 1,
        })),
      },
    ],
  })
}

const handleHoardingFlipButtonClick = () => {
  hoardingPlanStore.pathSegments = hoardingPlanStore.pathSegments.map(segment => ({
    ...segment,
    flipInsideDirection: !segment.flipInsideDirection,
  }))
}

const handleHoardingPlannerVisibilityUpdate = (event: { visibility: boolean }) => {
  isHoardingPlannerVisible.value = event.visibility
  hoardingPlannerState.value = event.visibility ? 'open' : 'closed'
}

const handleAnalyticsDataUpdate = (event: AnalyticsData) => {
  analyticsData.value = event
}
</script>

<template>
  <div
    data-testid="playwright-coverage-page"
    class="playwright-coverage-page"
  >
    <div class="coverage-shell">
      <aside
        class="coverage-nav-panel"
        data-testid="nav-coverage-panel"
      >
        <div class="coverage-panel-copy">
          <p class="coverage-eyebrow">Navigation Coverage</p>
          <h1 class="coverage-title">Playwright UI Smoke Harness</h1>
          <p class="coverage-subtitle">
            Deterministic browser proofs for scoped navigation, dialog, and drawer changes.
          </p>
        </div>

        <nav>
          <template
            v-for="item in navigationCoverageItems"
            :key="item.id"
          >
            <div
              v-if="item.kind === 'heading'"
              class="coverage-nav-heading"
              :data-testid="`nav-coverage-heading-${item.id}`"
            >
              {{ item.title }}
            </div>
            <a
              v-else
              :href="item.to"
              class="coverage-nav-link"
              :data-testid="`nav-coverage-link-${item.id}`"
            >
              <span>{{ item.title }}</span>
              <code>{{ item.to }}</code>
            </a>
          </template>
        </nav>
      </aside>

      <main class="coverage-main">
        <VCard
          class="coverage-card"
          data-testid="drawer-underlay-card"
        >
          <VCardItem>
            <VCardTitle>Drawer Layering Coverage</VCardTitle>
          </VCardItem>
          <VCardText>
            This card intentionally spans behind the temporary drawer so the browser proof can
            verify the drawer stays above normal card content.
          </VCardText>
          <VCardActions class="coverage-actions">
            <VBtn
              data-testid="drawer-open-btn"
              @click="handleDrawerOpenButtonClick"
            >
              Open Drawer
            </VBtn>
            <span data-testid="drawer-state">drawer: {{ drawerState }}</span>
          </VCardActions>
        </VCard>

        <VCard class="coverage-card">
          <VCardItem>
            <VCardTitle>Dialog Coverage</VCardTitle>
          </VCardItem>
          <VCardText>
            Open the dialog, assert the Vuetify container appears, then close it and confirm it is
            gone from the visible surface.
          </VCardText>
          <VCardActions class="coverage-actions">
            <VBtn
              data-testid="dialog-open-btn"
              color="primary"
              @click="handleDialogOpenButtonClick"
            >
              Open Dialog
            </VBtn>
            <span data-testid="dialog-state">dialog: {{ dialogState }}</span>
          </VCardActions>
        </VCard>

        <VCard class="coverage-card">
          <VCardItem>
            <VCardTitle>Customer Contact Drawer Coverage</VCardTitle>
          </VCardItem>
          <VCardText>
            Opens the real contact form so browser coverage can verify its long form scrolls to and
            saves from the normal submit action.
          </VCardText>
          <VCardActions class="coverage-actions">
            <VBtn
              data-testid="customer-contact-drawer-open-btn"
              color="primary"
              @click="handleCustomerContactDrawerOpenButtonClick"
            >
              Open Customer Contact Drawer
            </VBtn>
            <span data-testid="customer-contact-drawer-state">
              customer contact drawer: {{ customerContactDrawerState }}
            </span>
            <span data-testid="customer-contact-drawer-save-state">
              saved contact: {{ savedCustomerContactData?.firstName ?? 'not saved' }}
            </span>
          </VCardActions>
        </VCard>

        <VCard class="coverage-card">
          <VCardItem>
            <VCardTitle>Hoarding Planner Coverage</VCardTitle>
          </VCardItem>
          <VCardText>
            Deterministic fullscreen layout proof for the Hoarding planner 2D, 3D, and checklist
            views.
          </VCardText>
          <VCardActions class="coverage-actions">
            <VBtn
              data-testid="hoarding-planner-open-btn"
              color="primary"
              @click="handleHoardingPlannerOpenButtonClick"
            >
              Open Hoarding Planner
            </VBtn>
            <VBtn
              data-testid="hoarding-planner-open-angled-btn"
              color="secondary"
              @click="handleAngledHoardingPlannerOpenButtonClick"
            >
              Open Angled Hoarding Planner
            </VBtn>
            <VBtn
              data-testid="hoarding-planner-open-door-btn"
              color="secondary"
              @click="handleDoorHoardingPlannerOpenButtonClick"
            >
              Open Hoarding With Door
            </VBtn>
            <VBtn
              data-testid="hoarding-planner-open-print-checklist-btn"
              color="secondary"
              @click="handlePrintChecklistHoardingPlannerOpenButtonClick"
            >
              Open Hoarding With Long Frozen Checklist
            </VBtn>
            <VBtn
              data-testid="hoarding-planner-flip-btn"
              color="info"
              @click="handleHoardingFlipButtonClick"
            >
              Flip Inside Direction
            </VBtn>
            <span data-testid="hoarding-planner-state">
              hoarding planner: {{ hoardingPlannerState }}
            </span>
          </VCardActions>
        </VCard>

        <VCard class="coverage-card">
          <VCardItem>
            <VCardTitle>Calendar Drawer Coverage</VCardTitle>
          </VCardItem>
          <VCardText>
            Opens the real calendar event drawer with seeded store state so browser checks can
            verify fullscreen mobile placement and close behavior.
          </VCardText>
          <VCardActions class="coverage-actions">
            <VBtn
              data-testid="calendar-drawer-open-btn"
              color="info"
              @click="handleCalendarDrawerOpenButtonClick"
            >
              Open Calendar Drawer
            </VBtn>
            <VBtn
              data-testid="rule-drawer-open-btn"
              color="warning"
              @click="handleRuleDrawerOpenButtonClick"
            >
              Open Rules Drawer
            </VBtn>
            <span data-testid="calendar-drawer-state"
              >calendar drawer: {{ calendarDrawerState }}</span
            >
          </VCardActions>
        </VCard>

        <VCard class="coverage-card">
          <VCardItem>
            <VCardTitle>Product Events Drawer Coverage</VCardTitle>
          </VCardItem>
          <VCardText>
            Opens the real product events drawer with seeded store state so browser checks can
            verify fullscreen mobile placement and close behavior.
          </VCardText>
          <VCardActions class="coverage-actions">
            <VBtn
              data-testid="product-events-drawer-open-btn"
              color="error"
              @click="handleProductEventsDrawerOpenButtonClick"
            >
              Open Product Events Drawer
            </VBtn>
            <span data-testid="product-events-drawer-state">
              product events drawer: {{ productEventsDrawerState }}
            </span>
          </VCardActions>
        </VCard>

        <MessagesComposerCoverageHarness :prepare-calendar="seedCalendarCoverageStores" />

        <section
          class="coverage-asset-history"
          data-testid="asset-history-coverage"
        >
          <InspectionHistoryCard :asset-data="coverageAssetData" />
          <ServiceHistoryCard :asset-data="coverageAssetData" />
        </section>

        <section data-testid="calendar-linking-coverage">
          <LinkedCalendarEntriesCard :job-data="coverageCalendarJobData" />
        </section>

        <section data-testid="invoice-linking-coverage">
          <LinkedInvoicesCard :job-data="coverageCalendarJobData" />
        </section>

        <section data-testid="order-linking-coverage">
          <LinkedOrdersCard :job-data="coverageCalendarJobData" />
        </section>

        <section data-testid="quote-linking-coverage">
          <LinkedQuotesCard :job-data="coverageCalendarJobData" />
        </section>

        <section data-testid="recurring-quote-coverage">
          <VBtn @click="handleRecurringQuotesDialogOpenButtonClick"> Open quote schedules </VBtn>
        </section>

        <section data-testid="file-manager-coverage">
          <span data-testid="file-manager-request-count">
            requests: {{ fileManagerRequestCount }}
          </span>
          <FileManager
            entry-g-u-i-d="coverage-file-entry-guid"
            service-type="Projects"
            title="Project files"
            subtitle="Keep contracts, plans, and site media together."
          />
        </section>

        <section data-testid="user-invoice-coverage">
          <span data-testid="user-invoice-request-count">
            requests: {{ userInvoiceRequestCount }}
          </span>
          <UserInvoiceTable />
        </section>

        <section data-testid="billing-history-coverage">
          <div class="coverage-state-controls">
            <VBtn
              size="small"
              variant="tonal"
              @click="handleBillingHistoryReadyClick"
            >
              Ready billing
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleBillingHistoryLoadingClick"
            >
              Loading billing
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleBillingHistoryEmptyClick"
            >
              Empty billing
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleBillingHistoryErrorClick"
            >
              Failed billing
            </VBtn>
          </div>
          <span data-testid="billing-history-request-count">
            requests: {{ billingHistoryRequestCount }}
          </span>
          <BillingHistoryTable
            v-if="isBillingHistoryMounted"
            :key="billingHistoryCoverageKey"
          />
        </section>

        <section data-testid="user-project-coverage">
          <div class="coverage-state-controls">
            <VBtn
              size="small"
              variant="tonal"
              @click="handleProjectCoverageReadyClick"
            >
              Ready projects
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleProjectCoverageLoadingClick"
            >
              Loading projects
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleProjectCoverageEmptyClick"
            >
              Empty projects
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleProjectCoverageErrorClick"
            >
              Failed projects
            </VBtn>
          </div>
          <ProjectList
            :error-message="projectCoverageError"
            :is-loading="isProjectCoverageLoading"
            :projects="projectCoverageProjects"
          />
        </section>

        <section data-testid="account-device-coverage">
          <div class="coverage-state-controls">
            <VBtn
              size="small"
              variant="tonal"
              @click="handleDeviceCoverageReadyClick"
            >
              Ready sessions
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleDeviceCoverageLoadingClick"
            >
              Loading sessions
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleDeviceCoverageEmptyClick"
            >
              Empty sessions
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleDeviceCoverageErrorClick"
            >
              Failed sessions
            </VBtn>
          </div>
          <RecentDeviceLedger
            :devices="deviceCoverageDevices"
            :error-message="deviceCoverageError"
            :is-loading="isDeviceCoverageLoading"
          />
        </section>

        <section data-testid="user-account-project-coverage">
          <div class="coverage-state-controls">
            <VBtn
              size="small"
              variant="tonal"
              @click="handleAccountProjectCoverageReadyClick"
            >
              Ready assignments
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleAccountProjectCoverageLoadingClick"
            >
              Loading assignments
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleAccountProjectCoverageEmptyClick"
            >
              Empty assignments
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleAccountProjectCoverageErrorClick"
            >
              Failed assignments
            </VBtn>
          </div>
          <UserAccountProjectLedger
            :error-message="accountProjectCoverageError"
            :is-loading="isAccountProjectCoverageLoading"
            :projects="accountProjectCoverageProjects"
          />
        </section>

        <section data-testid="user-session-coverage">
          <div class="coverage-state-controls">
            <VBtn
              size="small"
              variant="tonal"
              @click="handleUserSessionCoverageReadyClick"
            >
              Ready user sessions
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleUserSessionCoverageLoadingClick"
            >
              Loading user sessions
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleUserSessionCoverageEmptyClick"
            >
              Empty user sessions
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleUserSessionCoverageErrorClick"
            >
              Failed user sessions
            </VBtn>
          </div>
          <UserSessionLedger
            :devices="userSessionCoverageDevices"
            :error-message="userSessionCoverageError"
            :is-loading="isUserSessionCoverageLoading"
          />
        </section>

        <section data-testid="user-billing-address-coverage">
          <div class="coverage-state-controls">
            <VBtn
              size="small"
              variant="tonal"
              @click="handleBillingAddressReadyClick"
            >
              Ready billing address
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleBillingAddressLoadingClick"
            >
              Loading billing address
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleBillingAddressEmptyClick"
            >
              Empty billing address
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleBillingAddressErrorClick"
            >
              Failed billing address
            </VBtn>
          </div>
          <span data-testid="user-billing-address-edit-count">
            edits: {{ billingAddressEditCount }}
          </span>
          <UserBillingAddressWorkspace
            :address="billingAddressCoverageValue"
            :error-message="billingAddressCoverageError"
            :is-loading="isBillingAddressCoverageLoading"
            @edit="handleBillingAddressEdit"
          />
        </section>

        <section data-testid="notification-preference-coverage">
          <div class="coverage-state-controls">
            <VBtn
              size="small"
              variant="tonal"
              @click="handleNotificationCoverageReadyClick"
            >
              Ready notification preferences
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleNotificationCoverageLoadingClick"
            >
              Loading notification preferences
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleNotificationCoverageEmptyClick"
            >
              Empty notification preferences
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleNotificationCoverageErrorClick"
            >
              Failed notification preferences
            </VBtn>
          </div>
          <span data-testid="notification-preference-save-count">
            saves: {{ notificationSaveCount }}
          </span>
          <UserNotificationPreferenceWorkspace
            :error-message="notificationCoverageError"
            :is-loading="isNotificationCoverageLoading"
            :preferences="visibleNotificationCoveragePreferences"
            @discard="handleNotificationCoverageReset"
            @save="handleNotificationCoverageSave"
            @toggle="handleNotificationCoverageToggle"
          />
          <AccountNotificationPreferenceWorkspace
            :error-message="notificationCoverageError"
            :is-loading="isNotificationCoverageLoading"
            :preferences="visibleNotificationCoveragePreferences"
            :timing="notificationTiming"
            @reset="handleNotificationCoverageReset"
            @save="handleNotificationCoverageSave"
            @toggle="handleNotificationCoverageToggle"
            @update:timing="handleNotificationTimingUpdate"
          />
        </section>

        <section data-testid="service-totals-coverage">
          <div class="coverage-state-controls">
            <VBtn
              size="small"
              variant="tonal"
              @click="handleServiceTotalsReadyClick"
            >
              Ready invoice worksheet
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleServiceTotalsLoadingClick"
            >
              Loading invoice worksheet
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleServiceTotalsEmptyClick"
            >
              Empty invoice worksheet
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handleServiceTotalsErrorClick"
            >
              Failed invoice worksheet
            </VBtn>
          </div>
          <ServiceTotalsForm
            :available-categories="['-', 'Labour', 'Materials']"
            :error-message="serviceTotalsCoverageError"
            :is-loading="isServiceTotalsCoverageLoading"
            :service-totals="visibleServiceTotalsCoverage"
            @update:service-totals="handleServiceTotalsUpdate"
          />
        </section>

        <section data-testid="payment-items-coverage">
          <div class="coverage-state-controls">
            <VBtn
              size="small"
              variant="tonal"
              @click="handlePaymentCoverageReadClick"
            >
              Read payment allocation
            </VBtn>
            <VBtn
              size="small"
              variant="tonal"
              @click="handlePaymentCoverageEditClick"
            >
              Edit payment allocation
            </VBtn>
          </div>
          <PaymentItemsSimple
            v-model:net="paymentCoverageNet"
            v-model:tax="paymentCoverageTax"
            :description="paymentCoverageDescription"
            :is-editing="isPaymentCoverageEditing"
            :messages-variables="paymentCoverageMessages"
            :service-data="paymentCoverageServiceData"
            @update:description="handlePaymentCoverageDescriptionUpdate"
          />
        </section>
      </main>
    </div>

    <footer class="coverage-footer">
      <Footer />
    </footer>

    <BusinessAnalyticsDrawer
      :analytics-data="analyticsData"
      :is-drawer-open="isCoverageDrawerOpen"
      @update:is-drawer-open="handleDrawerVisibilityUpdate"
      @update="handleAnalyticsDataUpdate"
    />

    <CalendarEventHandler
      :is-drawer-open="isCoverageCalendarDrawerOpen"
      drawer-name="Coverage Event"
      drawer-title="Coverage"
      :is-update="false"
      :is-rule="isCoverageCalendarRule"
      :event="coverageCalendarEvent"
      :drawer-tab-to-open="isCoverageCalendarRule ? 'allRules' : 'updateCreate'"
      @update:is-drawer-open="handleCalendarDrawerVisibilityUpdate"
      @event-added="closeCoverageCalendarDrawer"
      @event-updated="closeCoverageCalendarDrawer"
    />

    <CustomerContactDataDrawer
      :contact-data="coverageCustomerContactData"
      :is-drawer-open="isCoverageCustomerContactDrawerOpen"
      title="Coverage Customer Contact"
      subtitle="A tall contact form for deterministic drawer scroll coverage"
      @update:is-drawer-open="handleCustomerContactDrawerVisibilityUpdate"
      @update="handleCustomerContactDataUpdate"
    />

    <ProductEventsDrawer
      :is-drawer-open="isCoverageProductEventsDrawerOpen"
      product-g-u-i-d="coverage-product-guid"
      :order-data="coverageOrderData"
      @update:is-drawer-open="handleProductEventsDrawerVisibilityUpdate"
      @submit="closeCoverageProductEventsDrawer"
    />

    <ConfirmActionDialog
      :is-dialog-visible="isCoverageDialogVisible"
      title="Coverage Confirm Dialog"
      subtitle="Dialog smoke coverage for Playwright"
      description="Open and close this dialog to confirm the scoped browser checks stay wired."
      @update:has-agreed="handleDialogVisibilityUpdate"
    />

    <RecurringQuotesDialog
      :is-dialog-visible="isRecurringQuotesDialogVisible"
      @update:is-dialog-visible="handleRecurringQuotesDialogVisibilityUpdate"
    />

    <HoardingPlanDialog
      :is-dialog-visible="isHoardingPlannerVisible"
      :service-guid="null"
      :service-type="Endpoint.Quotes.Name"
      @update:is-dialog-visible="handleHoardingPlannerVisibilityUpdate"
    />
  </div>
</template>

<style scoped>
.playwright-coverage-page {
  min-height: 100vh;
  padding: 32px;
  background:
    radial-gradient(circle at top left, rgb(255 244 214 / 95%), transparent 40%),
    linear-gradient(135deg, #f7f1e8 0%, #dbe8f0 100%);
}

.coverage-shell {
  display: grid;
  gap: 24px;
  align-items: start;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
}

.coverage-nav-panel {
  position: sticky;
  top: 24px;
  padding: 24px;
  border: 1px solid rgb(31 41 55 / 10%);
  border-radius: 28px;
  background: rgb(255 255 255 / 88%);
  backdrop-filter: blur(10px);
  box-shadow: 0 24px 60px rgb(15 23 42 / 10%);
}

.coverage-panel-copy {
  margin-bottom: 20px;
}

.coverage-nav-panel nav {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.coverage-eyebrow {
  margin-bottom: 8px;
  color: #8a5a00;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.coverage-title {
  margin: 0 0 8px;
  color: #172033;
  font-size: clamp(1.8rem, 2.5vw, 2.4rem);
  line-height: 1.05;
}

.coverage-subtitle {
  margin: 0;
  color: rgb(23 32 51 / 72%);
}

.coverage-nav-link {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid rgb(23 32 51 / 10%);
  border-radius: 16px;
  color: #172033;
  text-decoration: none;
}

.coverage-nav-heading {
  color: #172033;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.coverage-nav-link code {
  color: rgb(23 32 51 / 60%);
  font-size: 0.8rem;
}

.coverage-main {
  display: grid;
  gap: 24px;
}

.coverage-state-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-block-end: 10px;
}

.coverage-footer {
  max-inline-size: 100%;
  margin-block-start: 24px;
  padding: 16px 24px;
  border: 1px solid rgb(31 41 55 / 10%);
  border-radius: 16px;
  background: rgb(255 255 255 / 88%);
  box-shadow: 0 24px 60px rgb(15 23 42 / 10%);
}

.coverage-card {
  min-height: 260px;
  border-radius: 28px;
  box-shadow: 0 24px 60px rgb(15 23 42 / 10%);
}

.coverage-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 0 16px 20px;
}

@media (max-width: 960px) {
  .playwright-coverage-page {
    padding: 20px;
  }

  .coverage-shell {
    grid-template-columns: 1fr;
  }

  .coverage-nav-panel {
    position: static;
  }
}
</style>
